import api from '@api';
import PercentageChart from '@components/charts/AudiencePercentageChart';
import IndexChart from '@components/charts/IndexChart';
import UniverseChart from '@components/charts/UniverseChart';
import AudienceTooltip from '@components/common/AudienceTooltip';
import Chip from '@components/common/Chip';
import Caption from '@components/layout/main/Caption';
import config from '@config';
import { css, Global } from '@emotion/react';
import { Metric } from '@enums';
import { useAudienceDetails } from '@hooks';
import ExportIcon from '@icons/export.svg?react';
import GWILogo from '@icons/gwi-logo.svg?react';
import TextMessageIcon from '@icons/text-message.svg?react';
import { useChatStore } from '@store/chat';
import { useShellStore } from '@store/shell';
import { themeOptions } from '@theme';
import { SecondaryButton as TextButton } from '@ui/buttons';
import type { DropdownMenuItemProps } from '@ui/dropdowns';
import { DropdownWithMenuItems } from '@ui/dropdowns';
import { Tooltip, type TooltipProps } from '@ui/tooltip';
import * as utils from '@utils';
import { getDataPointName, isOrigin } from '@utils';
import type { ForwardedRef } from 'react';
import {
  Fragment,
  forwardRef,
  startTransition,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import type { ChartData } from 'types/common';
import type { InsightChart } from 'types/schema';
import Styled from './InsightVisualization.styled';

const { palette } = themeOptions;

// Track which insights have already fired viewedSource analytics
// (module-scoped to persist across component remounts)
const trackedInsightIds = new Set<string>();

interface Props {
  hasCompactChart: boolean;
  data: InsightChart;
  index: number;
  prompt: string;
  onChangeMetric?: (metric: Metric) => void;
}

// biome-ignore lint/complexity/noExcessiveCognitiveComplexity: OK
function InsightVisualization(
  { hasCompactChart, data, index, prompt, onChangeMetric }: Props,
  ref: ForwardedRef<HTMLDivElement>
) {
  const chatId = useChatStore((state) => state.chat.id);
  const rightPanelProps = useShellStore((state) => state.rightPanelProps);
  const setToast = useShellStore((state) => state.setToast);
  const audienceDetails = useAudienceDetails(data.audiences);
  const [dataItemHeight, setDataItemHeight] = useState(0);
  const exportedContentRef = useRef<HTMLDivElement>(null);
  // Mobile only
  const [selectedDatapoint, setSelectedDatapoint] = useState<string>(null);
  const [selectedAudience, setSelectedAudience] =
    useState<ChartData['audience']>(null);
  const chartType = hasCompactChart ? 'compact' : 'regular';

  const {
    ['question_calculations']: questionCalculations,
    ['timeframe_groups']: timeframeGroups,
    ['location_groups']: locationGroups
  } = data;

  if (questionCalculations.length > 1) {
    console.warn(
      `The property 'question_calculations' has ${
        questionCalculations.length
      } items, while the app currently supports only one.`
    );
  }

  // All the required chart data props
  const [{ question, calculations, suffixes, ['metric_priority']: metrics }] =
    questionCalculations;

  const waves = timeframeGroups.map((timeframe) => timeframe.group);

  const locations = locationGroups.map((location) => location.group);

  const baseAudienceName = useMemo(() => {
    const labels = [
      ...new Set(
        calculations?.map((calc) => calc['base_audience_label']).filter(Boolean)
      )
    ];

    // No base labels in payload — fall back to "All Internet Users"
    if (labels.length === 0) {
      return null;
    }

    // All calculations share the same label — show it directly
    if (labels.length === 1) {
      return labels[0];
    }

    // Calculations have different labels — audiences come from different datasets
    return 'Multi-base';
  }, [calculations]);

  const hasAudience =
    Array.isArray(data.audiences) && data.audiences.length > 0;

  const hasMultipleAudiences =
    Array.isArray(data.audiences) && data.audiences.length > 1;

  const [selectedMetric, setSelectedMetric] = useState<Metric>(
    metrics ? (metrics[0] as Metric) : Metric.Index
  );

  // Unique question codes for analytics
  const questionCodes = Array.from(
    new Set(
      data.attributes.map(
        ({ ['attribute_key']: attributeKey }) =>
          `${attributeKey['namespace_code']}.${attributeKey['question_code']}`
      )
    )
  );

  // Question names for analytics in specific format
  const questionNames = questionCalculations.map(
    ({ question: { ['question_key']: key, ['question_name']: name } }) =>
      `${key['namespace_code']}.${key['question_code']} - ${name}`
  );

  // Unique namespace codes for analytics
  const namespaces = Array.from(
    new Set(
      data.attributes.map(
        (attribute) => attribute['attribute_key']['namespace_code']
      )
    )
  );

  const attributeCodes = data.attributes.map(
    ({ ['attribute_key']: attributeKey }) => {
      const namespaceCode = attributeKey['namespace_code'];
      const questionCode = attributeKey['question_code'];
      const datapointCode = attributeKey['datapoint_code'];

      return `${namespaceCode}.${questionCode}_${datapointCode}`;
    }
  );

  const metricOptions: DropdownMenuItemProps[] = [
    { label: 'Audience %', value: Metric.AudiencePercentage },
    { label: 'Universe', value: Metric.Universe },
    { label: 'Index', value: Metric.Index }
  ]
    .filter(({ value }) => metrics.includes(value))
    .map(({ label, value }) => ({
      label,
      value,
      selected: value === selectedMetric
    }));

  const { label: selectedMetricLabel } = metricOptions.find(
    ({ value }) => value === selectedMetric
  );

  const tooltipContent = (
    <Fragment>
      <b>This is the question we asked respondents:</b>
      <p>{question['question_description']}</p>
    </Fragment>
  );

  const content = question['question_description'] ? (
    <Tooltip
      {...tooltipProps}
      followCursor={!hasCompactChart}
      placement={!hasCompactChart ? 'bottom-start' : 'bottom'}
      content={tooltipContent}>
      <Styled.TooltipContent>
        <TextMessageIcon />
        <div className="insight-visualization-question">
          {question['question_description']}
          {suffixes && (
            <Styled.Caption className="hide-on-export" placement="top">
              Options: {suffixes}
            </Styled.Caption>
          )}
        </div>
      </Styled.TooltipContent>
    </Tooltip>
  ) : null;

  // Extra styles
  const styles = css`
    .visualization-tooltip {
      && { // Enforce priority
        font-size: ${utils.rem(12)};
        line-height: ${utils.rem(16)};
        max-width: 265px;
        ${!hasCompactChart && `margin: ${utils.rem(16)} 0 !important;`}
      }
    }
  `;

  // Extract the data item height to calculate the vertical
  // position of the label horizontal lines
  const setDataItemRef = (element: HTMLElement) => {
    if (element) {
      const { height } = element.getBoundingClientRect();

      setDataItemHeight(height);
    }
  };

  const metricProp =
    (selectedMetric === Metric.AudiencePercentage && 'percentage') ||
    (selectedMetric === Metric.Universe && 'size') ||
    'index';

  const memoizedCalculations = useMemo(() => {
    // Make it bulletproof
    if (!calculations) {
      return [];
    }

    if (hasMultipleAudiences) {
      return data.audiences
        .sort(({ title: a }, { title: b }) => (a < b ? -1 : 1))
        .map((audience) =>
          calculations.find((item) => item['audience_id'] === audience.id)
        );
    }

    return calculations;
  }, [calculations, hasMultipleAudiences, data.audiences]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: OK
  useEffect(() => {
    if (!rightPanelProps.isOpen) {
      trackedInsightIds.clear();
      return;
    }

    // Prevent duplicate tracking (persists across remounts)
    if (trackedInsightIds.has(data.insight.id)) {
      return;
    }

    trackedInsightIds.clear();

    // Add the current insight ID to the set to prevent duplicate tracking
    trackedInsightIds.add(data.insight.id);

    // Track event
    api.analytics.viewedSource({
      ['attributes_codes']: attributeCodes,
      ['audience_ids']: data.audiences.map((audience) => audience.id),
      ['audience_names']: data.audiences.map((audience) =>
        audience.title.trim()
      ),
      ['chat_id']: chatId,
      ['dataset_details']: data.datasets
        .map((dataset) => dataset.title)
        .join(', '),
      ['insight_id']: data.insight.id,
      locations,
      namespaces,
      ['nb_audiences']: data.audiences.length,
      query: prompt,
      ['question_codes']: questionCodes,
      ['question_names']: questionNames,
      ['source_text']: rightPanelProps.content,
      waves: data['timeframe_candidates'],
      ['chart_metric']:
        Metric.Universe === selectedMetric ? 'universe' : selectedMetric
    });
  }, [rightPanelProps]);

  // Match each calculation to the correct color
  const matchedAudiences = hasMultipleAudiences
    ? memoizedCalculations.map((item) => {
        const index = data.audiences.findIndex(
          (audience) => item?.['audience_id'] === audience.id
        );

        return {
          ...data.audiences[index],
          color: chartBarColors[index],
          id: data.audiences[index]?.id,
          title: data.audiences[index]?.title
        };
      })
    : null;

  // Map chart data
  const chartData = memoizedCalculations
    .map((item, index) => ({
      name: item
        ? getDataPointName(
            item.attribute,
            index,
            hasMultipleAudiences,
            suffixes,
            hasCompactChart
          )
        : '',
      value: item?.[metricProp],
      suffix: item?.attribute['suffix_name'],
      isOrigin: item && isOrigin(data, item, hasMultipleAudiences),
      audience: matchedAudiences?.[index]
    }))
    // Filtering should take place after mapping to maintain
    // the color matching with the tags in source overview!
    .filter(({ value }) => value)
    .sort((a, b) => b.value - a.value) satisfies ChartData[];

  const calcChartHeight = (metric?: Metric) => {
    if (hasCompactChart && metric !== Metric.Index) {
      return config.chart.compact.height;
    }

    let chartRowHeight = dataItemHeight || config.chart.row.defaultHeight;

    if (chartRowHeight >= config.chart.row.maxHeight) {
      chartRowHeight = config.chart.row.maxHeight;
    }

    return chartData.length * chartRowHeight;
  };

  const getChartColor = (colorName: string) => {
    if (colorName in palette) {
      const color = palette[colorName];

      return String('medium' in color ? color['medium'] : color['500']);
    }

    console.warn(`Color '${colorName}' not found.`);
  };

  const chartColors: string[] = chartData.map(({ audience }) =>
    getChartColor(audience ? audience.color : chartBarColors[0])
  );

  const handleSelectDataPoint = (data: ChartData) => {
    if (!hasCompactChart) {
      return;
    }

    if (hasMultipleAudiences) {
      setSelectedDatapoint(chartData[0].name);
      setSelectedAudience(data.audience);
      utils.log(`Selected audience '${data.audience.title}'.`);
    } else {
      setSelectedDatapoint(data.name);
      utils.log(`Selected data point '${data.name}'.`);
    }
  };

  const chartProps = {
    barSize: 16,
    colors: chartColors,
    compact: hasCompactChart,
    data: chartData,
    gridHorizontal: hasCompactChart || hasMultipleAudiences,
    onSelectDataPoint: handleSelectDataPoint
  };

  const chart =
    (selectedMetric === Metric.Index && (
      <IndexChart {...chartProps} height={calcChartHeight(Metric.Index)} />
    )) ||
    (selectedMetric === Metric.Universe && (
      <UniverseChart
        {...chartProps}
        height={calcChartHeight(Metric.Universe)}
      />
    )) ||
    (selectedMetric === Metric.AudiencePercentage && (
      <PercentageChart
        {...chartProps}
        height={calcChartHeight(Metric.AudiencePercentage)}
      />
    ));

  const dataPoints = hasMultipleAudiences ? (
    chartData[0]?.name.split('✂️').shift()
  ) : (
    <Fragment>
      <Styled.DataList>
        {chartData.map(({ name, value, suffix }) => (
          <Styled.DataListItem
            ref={setDataItemRef}
            key={utils.generateKey(`datapoint-${name}-${value}`)}>
            <Styled.DataTitle data-testid="insight-visualization-data-list-item-title">
              {name}
            </Styled.DataTitle>
            <Styled.DataCaption data-testid="insight-visualization-data-list-item-suffix">
              {suffix}
            </Styled.DataCaption>
          </Styled.DataListItem>
        ))}
      </Styled.DataList>
      {!hasCompactChart &&
        Array.from({
          length: chartData.length
        }).map((_, index) => (
          <Styled.Separator
            x={index + 1 === chartData.length ? '50%' : 0}
            y={(index + 1) * dataItemHeight}
            key={utils.generateKey(
              `separator-${data.insight.id}-${selectedMetric}-${index}`
            )}
          />
        ))}
    </Fragment>
  );

  const getAudienceList = () => {
    if (!hasCompactChart) {
      return chartData
        .map(({ audience }) => audience)
        .filter((audience) => audience.title);
    }

    // Mobile only
    if (selectedAudience) {
      return [selectedAudience];
    }

    return (
      chartData
        // It is possible to have more than one audiences
        // related to the question of the insight
        // .filter(({ isOrigin }) => isOrigin)
        .map(({ audience }) => audience)
    );
  };

  const dataKeys = (
    <Styled.DataKeyList>
      {hasAudience ? (
        <Fragment>
          {/* {selectedMetric === Metric.Index && (
            <Chip color={themeOptions.palette.common.black}>Average</Chip>
          )} */}
          {hasMultipleAudiences ? (
            getAudienceList().map((audience) => (
              <Chip
                className="audience-chip"
                color={getChartColor(audience.color)}
                key={utils.generateKey(
                  `datakey-${data.insight.id}-${audience.id}`
                )}
                tooltip={
                  <AudienceTooltip
                    audience={{
                      ...((audienceDetails || []).find(
                        ({ id }) => id === audience.id
                      ) || audience),
                      base: baseAudienceName
                    }}
                    color={getChartColor(audience.color)}
                  />
                }>
                {audience.title}
              </Chip>
            ))
          ) : (
            <Chip
              className="audience-chip"
              color={getChartColor(chartBarColors[0])}
              tooltip={
                <AudienceTooltip
                  audience={{
                    ...((audienceDetails || []).find(
                      ({ id }) => id === data.audiences[0]?.id
                    ) || data.audiences[0]),
                    base: baseAudienceName
                  }}
                  color={getChartColor(chartBarColors[0])}
                />
              }>
              {data.audiences[0].title}
            </Chip>
          )}
        </Fragment>
      ) : (
        <Chip color={chartColors[0]}>All internet users</Chip>
      )}
    </Styled.DataKeyList>
  );

  const handleChangeMetric = (value: Metric) => {
    startTransition(() => {
      setSelectedMetric(value);
      // Reset the selected datapoint on metric change
      setSelectedDatapoint(null);
      onChangeMetric?.(value);
    });
  };

  const handleExportInsight = async (data: InsightChart) => {
    try {
      await utils.exportAsImage(
        exportedContentRef.current,
        chartType,
        `GWI_${question['question_name']
          .replace(/[:,']/g, '')
          .replace(/[_\s]/g, '-')
          .toLowerCase()}`
      );

      // Track event
      api.analytics.exportedInsightAsImage({
        ['chat_id']: chatId,
        ['audience_ids']: data.audiences.map((audience) => audience.id),
        ['audience_names']: data.audiences.map((audience) => audience.title),
        ['attributes_codes']: attributeCodes,
        ['chart_metric']:
          Metric.Universe === selectedMetric ? 'universe' : selectedMetric,
        ['dataset_details']: data.datasets
          .map((dataset) => dataset.title)
          .join(', '),
        locations,
        namespaces,
        ['question_codes']: questionCodes,
        ['question_names']: questionNames,
        ['waves']: data['timeframe_candidates']
      });
    } catch (error) {
      setToast({
        status: 'error',
        message: 'Failed to export chart.'
      });
      console.error('Failed to export chart:', error);
    }
  };

  const header =
    metricOptions.length > 1 ? (
      <Fragment>
        <DropdownWithMenuItems
          className="dropdown-menu hide-on-export"
          triggerLabel={selectedMetricLabel}
          testId="metrics-dropdown-menu"
          options={metricOptions}
          onChange={handleChangeMetric}
        />
        <div className="show-on-export">{selectedMetricLabel}</div>
      </Fragment>
    ) : (
      metricOptions[0].label
    );

  const title = (
    <Styled.Title data-testid="insight-visualization-title">
      {question['question_name']}
    </Styled.Title>
  );

  const footer = (
    <Fragment>
      {hasCompactChart && selectedDatapoint && (
        <Styled.SelectedDataPoint>
          <Styled.KeysLabel>Datapoint:</Styled.KeysLabel>{' '}
          {selectedDatapoint.split('✂️').shift()}
        </Styled.SelectedDataPoint>
      )}
      <Styled.Keys>
        <Styled.KeysLabel className="fixed-height">Audience:</Styled.KeysLabel>{' '}
        {dataKeys}
      </Styled.Keys>
      <Styled.MetadataGroup
        className="hide-on-export"
        data-testid="insight-visualization-source">
        <Caption
          data={{
            ['base_audience']: baseAudienceName,
            ['datasets']: data['datasets'],
            ['timeframe_groups']: data['timeframe_groups'],
            ['location_groups']: data['location_groups']
          }}
          shouldOpenDialog={hasCompactChart}
        />
      </Styled.MetadataGroup>
    </Fragment>
  );

  const table =
    chartData.length > 0 ? (
      <Styled.Table
        className="insight-visualization-table"
        data-testid="explore-table">
        <Styled.TableRow>
          <Styled.TableHeader data-testid="insight-visualization-table-header">
            Survey Response (Data Point)
          </Styled.TableHeader>
          <Styled.TableHeader alignContent="center">
            {header}
          </Styled.TableHeader>
        </Styled.TableRow>
        <Styled.TableRow>
          <Styled.TableData>{dataPoints}</Styled.TableData>
          <Styled.TableData data-testid="insight-visualization-table-chart">
            {chart}
          </Styled.TableData>
        </Styled.TableRow>
        <Styled.TableFooter data-testid="insight-visualization-table-footer">
          {footer}
        </Styled.TableFooter>
      </Styled.Table>
    ) : (
      <Styled.Notice>Sorry, no chart available!</Styled.Notice>
    );

  // Initialize the data point name for mobile devices
  if (hasCompactChart && selectedDatapoint === null) {
    const singleDatapoint = chartData[0]?.name;

    setSelectedDatapoint(
      hasMultipleAudiences
        ? singleDatapoint
        : chartData.find(({ isOrigin }) => isOrigin)?.name || singleDatapoint
    );
  }

  const sourceInfo = (
    <Styled.SourceInfo>
      <Styled.MetaList>
        <Styled.MetaLabel>Base audience:</Styled.MetaLabel>
        <Styled.MetaValue>{baseAudienceName}</Styled.MetaValue>
        <Styled.MetaLabel>Data set:</Styled.MetaLabel>
        <Styled.MetaValue>
          {data['datasets'].map((dataset) => dataset.title).join(', ')}
        </Styled.MetaValue>
        <Styled.MetaLabel>Timeframe:</Styled.MetaLabel>
        <Styled.MetaValue>{waves.join(', ')}</Styled.MetaValue>
        <Styled.MetaLabel>Location:</Styled.MetaLabel>
        <Styled.MetaValue>{locations.join(', ')}</Styled.MetaValue>
      </Styled.MetaList>
      <Styled.Logo>
        <GWILogo />
      </Styled.Logo>
    </Styled.SourceInfo>
  );

  const exportedContent = (
    <Styled.ExportedContent
      ref={exportedContentRef}
      width={config.chart.imageExport[chartType].width}>
      {title}
      <Styled.Content>{content}</Styled.Content>
      {table}
      {sourceInfo}
    </Styled.ExportedContent>
  );

  return (
    <Fragment>
      {/* Required to override styles because tooltip is a React Portal */}
      <Global styles={styles} />
      <Styled.Wrapper
        className="insight-visualization"
        data-index={index}
        data-testid="insight-visualization"
        ref={ref}>
        {title}
        <Styled.Content data-testid="insight-visualization-question">
          {content}
        </Styled.Content>
        {table}
        <Styled.Footer>
          <TextButton
            className="button"
            testId="export-chart-button"
            startIcon={<ExportIcon />}
            onClick={() => handleExportInsight(data)}>
            Export PNG
          </TextButton>
        </Styled.Footer>
      </Styled.Wrapper>
      {exportedContent}
    </Fragment>
  );
}

const chartBarColors = [
  'violet',
  'teal',
  'pink',
  'blue',
  'yellow',
  'purple',
  'green',
  'red',
  'cyan',
  'orange'
] as const;

const tooltipProps: TooltipProps = {
  classesTooltip: 'visualization-tooltip',
  content: null,
  children: null,
  enterDelay: 0,
  enterNextDelay: 250,
  enterTouchDelay: 350,
  leaveTouchDelay: 5000,
  leaveDelay: 250,
  variant: 'navy'
};

export default forwardRef(InsightVisualization);
